import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight } from '@phosphor-icons/react';
import { useLanguage } from '@/context/LanguageContext';
import { TID } from '@/lib/testIds';
import { IMAGES } from '@/lib/images';
import { scrollToId } from '@/components/site/Navbar';

export default function WhoWeAre() {
  const { t } = useLanguage();
  return (
    <section
      id="who-we-are"
      data-testid={TID.who.root}
      className="relative bg-[#F1F5F9] text-[#0F172A]"
    >
      <div className="absolute inset-0 bg-grid-lines-light pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <div className="relative aspect-[4/5] overflow-hidden border border-black/10">
            <img
              src={IMAGES.whoWeAreLab}
              alt="Defense research laboratory"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/40 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5 flex items-center justify-between font-mono text-[10px] tracking-[0.25em] uppercase text-white">
              <span>FIELD / R&amp;D</span>
              <span>EU-RO-2026</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 order-1 lg:order-2 flex flex-col">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3"
          >
            <span className="w-8 h-px bg-[#4F6355]" />
            <span className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#4F6355]">
              {t.who.overline}
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display font-bold tracking-tighter mt-6 text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] leading-[1.05]"
          >
            {t.who.title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-base md:text-lg text-[#475569] leading-relaxed max-w-2xl"
          >
            {t.who.body}
          </motion.p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
            {t.who.highlights.map((h, idx) => (
              <motion.div
                key={h}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
                className="flex items-center gap-3 py-2 border-b border-black/10"
              >
                <CheckCircle weight="duotone" size={18} className="text-[#4F6355] shrink-0" />
                <span className="text-sm font-semibold text-[#0F172A] tracking-tight">{h}</span>
              </motion.div>
            ))}
          </div>

          <div className="mt-10">
            <button
              data-testid={TID.who.cta}
              onClick={() => scrollToId('contact')}
              className="btn-outline-light inline-flex items-center gap-3 px-6 py-3.5 text-[11px] tracking-[0.22em] uppercase font-bold"
            >
              {t.cta.learnMore}
              <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
